import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Note } from '../../../components/performance/evaluation/EvaluationParts';
import { formatNum, metaOfContext } from '../../../components/performance/evaluation/evaluationAnalytics';
import { BIN, NS, fetchScatter } from '../config';

// E06 orta sol — öz puan (X) × yönetici puanı (Y) saçılımı; eşitlik çizgisi ve büyük fark sınırları (|fark| = T). Noktalar kişi
// değil 0,25'lik KOVADIR (nokta boyu = kişi sayısı): yüz bin kişilik kiracıda istemciye kişi satırı gitmez. Nokta (ya da tablo
// satırı) → o kovadaki çalışanlar listesi (oradan E14 görüşme sekmesi). Renk yönle (öz yüksek / yakın / yönetici yüksek) ve
// metinle (ipucu, tablo) desteklenir.
const MIN = 1;
const MAX = 5;

export default function ScatterPanel({
  filters, screen, onOpen, onMeta,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const key = JSON.stringify([filters, screen]);
  const panel = usePanel(() => fetchScatter(filters, screen), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('scatter', metaOfContext(panel.data)); }, [panel.data, onMeta]);
  const bins = panel.data?.bins || [];
  const delta = Number(panel.data?.delta ?? 0.5);
  const large = Number(panel.data?.largeThreshold ?? 1);
  const half = BIN / 2;
  const data = bins.map((b) => ({
    ...b, x: Number(b.self) + half, y: Number(b.manager) + half, z: b.n,
  }));
  const toneOf = (d) => {
    const gap = d.y - d.x;
    if (Math.abs(gap) <= delta) return theme.colors.primary.main;
    return gap > 0 ? theme.colors.secondary.dark : theme.colors.auxiliar.danger;
  };
  const range = (from) => `${formatNum(from, lang)}–${formatNum(Number(from) + BIN - 0.01, lang)}`;

  return (
    <PanelCard
      panelKey="scatter" title={t(`${NS}.panel.scatter`)} span={7}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={bins.length === 0}
      emptyText={t(`${NS}.scatter.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <Note>{t(`${NS}.scatter.hint`, { n: formatNum(panel.data?.pairsN, lang, 0), threshold: formatNum(large, lang) })}</Note>
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.self`)}</th>
              <th>{t(`${NS}.col.manager`)}</th>
              <th>{t(`${NS}.col.n`)}</th>
            </tr>
          </thead>
          <tbody>
            {bins.map((b) => (
              <tr key={`${b.self}:${b.manager}`} data-row={`${b.self}:${b.manager}`}>
                <td><Clickable onClick={() => onOpen(b)} data-open-bin={`${b.self}:${b.manager}`}>{range(b.self)}</Clickable></td>
                <td>{range(b.manager)}</td>
                <td>{formatNum(b.n, lang, 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="x" name={t(`${NS}.col.self`)} domain={[MIN, MAX]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: theme.metrics.font.size.xs }} label={{ value: t(`${NS}.col.self`), position: 'insideBottom', offset: -4, fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="number" dataKey="y" name={t(`${NS}.col.manager`)} domain={[MIN, MAX]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: theme.metrics.font.size.xs }} label={{ value: t(`${NS}.col.manager`), angle: -90, position: 'insideLeft', fontSize: theme.metrics.font.size.xs }} />
              <ZAxis type="number" dataKey="z" range={[40, 400]} name={t(`${NS}.col.n`)} />
              <ReferenceLine segment={[{ x: MIN, y: MIN }, { x: MAX, y: MAX }]} stroke={theme.colors.text.muted} strokeDasharray="4 4" ifOverflow="hidden" />
              <ReferenceLine segment={[{ x: MIN, y: MIN + large }, { x: MAX - large, y: MAX }]} stroke={theme.colors.auxiliar.warning} ifOverflow="hidden" />
              <ReferenceLine segment={[{ x: MIN + large, y: MIN }, { x: MAX, y: MAX - large }]} stroke={theme.colors.auxiliar.warning} ifOverflow="hidden" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value, name, item) => {
                  const p = item?.payload || {};
                  if (name === t(`${NS}.col.self`)) return [range(p.self), name];
                  if (name === t(`${NS}.col.manager`)) return [range(p.manager), name];
                  return [formatNum(value, lang, 0), name];
                }}
              />
              <Scatter data={data} cursor="pointer" onClick={(d) => onOpen(d.payload || d)}>
                {data.map((d) => <Cell key={`${d.self}:${d.manager}`} fill={toneOf(d)} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}
